import { useEffect, useState } from 'react'
import { StarIcon } from '@heroicons/react/20/solid'
import { CheckCircleIcon } from '@heroicons/react/24/outline'

import { testimonials } from '@/data/testimonials'

const ROTATE_INTERVAL_MS = 7000

// Mirrors COLORS_EXPENSE in utils/chartUtils.tsx so the mock report cards
// read as the real reports tab once blurred.
const CHART_COLORS = ['#FF8042', '#FFBB28', '#4ECDC4', '#45B7D1', '#C742FF']

function MockCard({
  title,
  className,
  children,
}: {
  title: string
  className?: string
  children: React.ReactNode
}) {
  return (
    <div
      className={`absolute rounded-2xl border border-gray-100 bg-white p-5 shadow-lg shadow-blue-900/5 ${className ?? ''}`}
      aria-hidden="true"
    >
      <p className="text-xs font-semibold text-gray-700">{title}</p>
      <div className="mt-3">{children}</div>
    </div>
  )
}

function MockBarChart() {
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug']
  // stacked segment heights per month, bottom-up, in SVG units
  const stacks = [
    [26, 14, 8],
    [34, 10, 12],
    [22, 18, 6],
    [40, 12, 10],
    [30, 22, 8],
    [24, 12, 16],
    [44, 16, 10],
    [36, 20, 12],
  ]
  return (
    <svg viewBox="0 0 300 150" className="w-full">
      {[0, 1, 2, 3].map((i) => (
        <line
          key={i}
          x1="28"
          x2="296"
          y1={16 + i * 36}
          y2={16 + i * 36}
          stroke="#f3f4f6"
          strokeDasharray="3 3"
        />
      ))}
      {stacks.map((segments, monthIndex) => {
        const x = 40 + monthIndex * 33
        let y = 124
        return (
          <g key={monthIndex}>
            {segments.map((height, segmentIndex) => {
              y -= height
              return (
                <rect
                  key={segmentIndex}
                  x={x}
                  y={y}
                  width="16"
                  height={height - 1.5}
                  rx="1.5"
                  fill={CHART_COLORS[segmentIndex]}
                />
              )
            })}
            <text
              x={x + 8}
              y={140}
              textAnchor="middle"
              fontSize="10"
              fill="#6b7280"
            >
              {months[monthIndex]}
            </text>
          </g>
        )
      })}
    </svg>
  )
}

function MockDonutChart() {
  // percentages per category, drawn as stroke arcs on a circle of circumference 100
  const segments = [
    { label: 'Software', value: 34 },
    { label: 'Travel', value: 26 },
    { label: 'Meals', value: 18 },
    { label: 'Office', value: 13 },
    { label: 'Other', value: 9 },
  ]
  let offset = 25 // start at 12 o'clock
  return (
    <div className="flex items-center gap-4">
      <svg viewBox="0 0 42 42" className="h-28 w-28 shrink-0">
        {segments.map((segment, index) => {
          const dash = `${segment.value - 1} ${100 - segment.value + 1}`
          const circle = (
            <circle
              key={segment.label}
              cx="21"
              cy="21"
              r="15.915"
              fill="none"
              stroke={CHART_COLORS[index]}
              strokeWidth="6"
              strokeDasharray={dash}
              strokeDashoffset={offset}
            />
          )
          offset -= segment.value
          return circle
        })}
      </svg>
      <ul className="space-y-1.5">
        {segments.slice(0, 4).map((segment, index) => (
          <li key={segment.label} className="flex items-center gap-2 text-[11px] text-gray-600">
            <span
              className="h-2 w-2 shrink-0 rounded-full"
              style={{ backgroundColor: CHART_COLORS[index] }}
            />
            {segment.label}
            <span className="ml-auto pl-3 font-medium text-gray-900">{segment.value}%</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

function MockTrendChart() {
  return (
    <svg viewBox="0 0 220 90" className="w-full">
      <defs>
        <linearGradient id="auth-trend-fill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#00C49F" stopOpacity="0.25" />
          <stop offset="100%" stopColor="#00C49F" stopOpacity="0" />
        </linearGradient>
      </defs>
      {[0, 1, 2].map((i) => (
        <line
          key={i}
          x1="4"
          x2="216"
          y1={14 + i * 28}
          y2={14 + i * 28}
          stroke="#f3f4f6"
          strokeDasharray="3 3"
        />
      ))}
      <path
        d="M4 66 C 30 58, 48 70, 68 60 S 108 30, 128 38 S 168 20, 216 14 L 216 86 L 4 86 Z"
        fill="url(#auth-trend-fill)"
      />
      <path
        d="M4 66 C 30 58, 48 70, 68 60 S 108 30, 128 38 S 168 20, 216 14"
        fill="none"
        stroke="#00C49F"
        strokeWidth="2.5"
        strokeLinecap="round"
      />
    </svg>
  )
}

function MockKpiRow() {
  const stats = [
    { label: 'Total expenses', value: '$4,820.32' },
    { label: 'Receipts matched', value: '96%' },
    { label: 'This month', value: '$612.40' },
  ]
  return (
    <div className="flex gap-8">
      {stats.map((stat) => (
        <div key={stat.label}>
          <p className="whitespace-nowrap text-[11px] text-gray-500">{stat.label}</p>
          <p className="mt-1 whitespace-nowrap text-lg font-semibold tracking-tight text-gray-900">
            {stat.value}
          </p>
        </div>
      ))}
    </div>
  )
}

function ReportsBackdrop() {
  return (
    <div className="absolute inset-0 blur-[3px]" aria-hidden="true">
      <MockCard title="Monthly spend" className="-left-10 top-8 w-96 -rotate-2">
        <MockBarChart />
      </MockCard>
      <MockCard title="Expenses by category" className="-right-14 top-24 w-80 rotate-2">
        <MockDonutChart />
      </MockCard>
      <MockCard title="Overview" className="-left-6 bottom-24 rotate-1">
        <MockKpiRow />
      </MockCard>
      <MockCard title="Spend trend" className="-right-10 bottom-6 w-72 -rotate-1">
        <MockTrendChart />
      </MockCard>
    </div>
  )
}

export function AuthTestimonialPanel() {
  const [activeIndex, setActiveIndex] = useState(0)

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
    const timer = setInterval(
      () => setActiveIndex((index) => (index + 1) % testimonials.length),
      ROTATE_INTERVAL_MS,
    )
    return () => clearInterval(timer)
  }, [activeIndex])

  return (
    <div className="relative h-full w-full overflow-hidden bg-gradient-to-br from-blue-50 via-white to-blue-100/70 border-l border-gray-100">
      <div
        className="absolute -top-32 right-10 h-96 w-96 rounded-full bg-blue-200/40 blur-3xl"
        aria-hidden="true"
      />
      <div
        className="absolute -bottom-32 -left-16 h-96 w-96 rounded-full bg-blue-100/50 blur-3xl"
        aria-hidden="true"
      />

      <ReportsBackdrop />

      {/* veil so the testimonial card owns the foreground */}
      <div
        className="absolute inset-0 bg-gradient-to-b from-white/30 via-white/10 to-white/40"
        aria-hidden="true"
      />

      <div className="relative flex h-full items-center justify-center px-12 xl:px-20">
        <div className="w-full max-w-md rounded-2xl bg-white p-8 shadow-xl shadow-blue-900/10 ring-1 ring-gray-900/5">
          <div className="relative min-h-[15rem]">
            {testimonials.map((testimonial, index) => (
              <figure
                key={testimonial.name}
                className={`absolute inset-0 flex flex-col transition-opacity duration-700 ease-in-out ${
                  index === activeIndex ? 'opacity-100' : 'pointer-events-none opacity-0'
                }`}
                aria-hidden={index !== activeIndex}
              >
                <div className="flex gap-1" aria-label="5 out of 5 stars">
                  {[...Array(5)].map((_, starIndex) => (
                    <StarIcon key={starIndex} className="h-5 w-5 text-amber-400" aria-hidden="true" />
                  ))}
                </div>
                <blockquote className="mt-5">
                  <p className="text-xl font-semibold leading-snug tracking-tight text-gray-900">
                    &ldquo;{testimonial.title}&rdquo;
                  </p>
                  <p className="mt-3 text-sm leading-6 text-gray-600">{testimonial.body}</p>
                </blockquote>
                <figcaption className="mt-auto flex items-center gap-3 pt-5">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full bg-blue-100 text-sm font-semibold text-blue-700 ring-1 ring-gray-900/5">
                    {testimonial.image ? (
                      <img className="h-full w-full object-cover" src={testimonial.image} alt="" />
                    ) : (
                      testimonial.name.charAt(0)
                    )}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">{testimonial.name}</p>
                    <div className="mt-0.5 flex items-center gap-1 text-gray-400">
                      <CheckCircleIcon className="h-4 w-4" aria-hidden="true" />
                      <span className="text-xs">Verified Purchase</span>
                    </div>
                  </div>
                </figcaption>
              </figure>
            ))}
          </div>

          <div className="mt-6 flex gap-2">
            {testimonials.map((testimonial, index) => (
              <button
                key={testimonial.name}
                type="button"
                onClick={() => setActiveIndex(index)}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  index === activeIndex ? 'w-6 bg-blue-600' : 'w-1.5 bg-gray-300 hover:bg-gray-400'
                }`}
                aria-label={`Show testimonial from ${testimonial.name}`}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
